import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { ResendIcon } from "@components/icon";
import { DateRangeSpecialValue, TDisabledDateUnit } from "@components/inputs/date/popup/Calendar.utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { SwitchType } from "@components/inputs/switch/Switch";
import {
    getSimpleBoolSelectItems,
    ifAll,
    ifAny,
    IFieldDefFn,
    IGetValueArgs,
    ITemporalPropertySettings,
    not,
    TGetValueFn,
    TTemporalDialogSettings
} from "@components/smart/FieldInfo";
import {
    AttachmentsDef,
    BirthNumberFieldDef,
    BirthNumberFormatter,
    countryDef,
    EmailDef,
    emailFormatter,
    getAddressFields,
    getAddressGroup,
    getAttachmentAdditionalProps,
    getAttachmentsColumnDef,
    getAttachmentsFilterDef,
    getLabelsDef,
    getLabelsFilterDef,
    PhoneNumberDef,
    phoneNumberFormatter
} from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { TCellValue } from "@components/table";
import {
    CompanyRoleEntity,
    EntitySetName,
    EntityTypeName,
    ForeignInsuranceCarrierAddressEntity,
    IPrDeductionEntity,
    IPrEmployeeEntity,
    IPrEmployeeTemporalEntity,
    IPrEmploymentEntity,
    IUserEntity,
    IUserStatusEntity,
    LORegionalOfficeEntity,
    MpsvFreeAccessToLaborMarketTypeEntity,
    PrDeductionEntity,
    PrDeductionTemporalEntity,
    PrDeductionTypeEntity,
    PrDisabilityTypeEntity,
    PrEmployeeContactAddressEntity,
    PrEmployeeCzechAddressEntity,
    PrEmployeeDependentPersonEntity,
    PrEmployeeEntity,
    PrEmployeePermanentAddressEntity,
    PrEmployeeTaxResidenceAddressEntity,
    PrEmployeeTemporalEntity,
    PrEmploymentEntity,
    PrEmploymentTypeEntity,
    PrForeignInsuranceCarrierEntity,
    PrForeignInsuranceCarrierSectorTypeEntity,
    PrHealthInsuranceCompanyEntity,
    PrIdentityDocumentTypeEntity,
    PrLaborMarketAccessEntity,
    PrPensionTypeEntity,
    PrSexEntity,
    PrSocialInsuranceDiscountTypeEntity,
    PrSubmissionEntity,
    PrWorkPermitTypeEntity,
    UserCompanyRoleEntity,
    UserEntity
} from "@odata/GeneratedEntityTypes";
import {
    CountryCode,
    PrEmployeeDependentPersonRelationCode,
    PrEmployeeStateCode,
    PrForeignInsuranceCarrierSectorTypeCode,
    PrForeignInsuranceCarrierTypeCode,
    PrIdentityDocumentTypeCode,
    PrMinimumHealthInsuranceTypeCode,
    PrPensionTypeCode,
    PrSubmissionTypeCode,
    PrWorkPermitTypeCode,
    UserStatusCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName, getEnumSelectItems } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import {
    getTemporalAdditionalProperties,
    getTemporalPropertyFieldDefinition,
    ISmartTemporalPropertyDialogCustomData,
    TemporalValidityFieldsDef
} from "@odata/TemporalUtils";
import { resendUserInvitation } from "@pages/admin/users/Users.shared.utils";
import { getUserStatusColor } from "@pages/admin/users/Users.utils";
import { ATTACHMENTS_COLUMN_LOCAL_CONTEXT } from "@pages/documents/Document.utils";
import { EmploymentStatus, getEmploymentStatus } from "@pages/payroll/employment/Employment.utils";
import { educationDef } from "@pages/payroll/payroll.utils";
import { getSubmissionTableDefinitionForFormTabs } from "@pages/payroll/submission/GetSubmissionTableDefinitionForFormTabs";
import { isNewEntity } from "@utils/definition.utils";
import { generatePassword, isDefined, isNotDefined } from "@utils/general";
import { Dayjs } from "dayjs";
import i18next from "i18next";
import React from "react";
import { DefaultTheme } from "styled-components";

import Field from "../../../components/inputs/field";
import FieldsWrapper from "../../../components/inputs/field/FieldsWrapper";
import SmartField from "../../../components/smart/smartField";
import { DASH_CHARACTER } from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, GroupedField, LabelStatus, Sort, TextAlign } from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import BindingContext, { createPath } from "../../../odata/BindingContext";
import { ROUTE_DEDUCTIONS, ROUTE_EMPLOYMENT } from "../../../routes";
import { DATE_MAX, getUtcDayjs } from "../../../types/Date";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { userNameWithAvatarFormatter, userNameWithAvatarTableFormatter } from "../../admin/users/Users.utils";
import {
    getBankAccountFieldsDef,
    paymentDetailFields,
    SAVED_ACCOUNTS_PATH
} from "../../banks/bankAccounts/BankAccounts.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import {
    getNumberOursSummaryDef,
    getNumberRangeFieldDefs,
    NumberRangeAdditionalProperties
} from "../../numberRange/NumberRange.utils";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import {
    getForeignTaxIdentifierTypeDef,
    IEmployeeFormCustomData,
    IPrEmployeeEntityExtended,
    OtherIdentifierFields,
    PreviousEmploymentDurationDaysPath,
    PreviousEmploymentDurationYearsPath,
    SEND_EMPLOYEE_INVITATION_ACTION,
    SEND_PAYSLIP_PASSWORD_ACTION,
    ShowContactAddressPath,
    ShowCzechAddressPath,
    ShowDisabilityPath,
    ShowForeignInsuranceCarrierPath,
    ShowOtherIdentifiers,
    ShowPensionPath,
    ShowSocialInsuranceDiscountLocalPath,
    ShowStudyPath,
    TAX_PAYER_DECLARATION_ACTION,
    transformWorkPermitTypeItems
} from "./Employee.utils";
import EmployeeFormView from "./EmployeeFormView";

const isContactAddressVisible: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(ShowContactAddressPath);
};

const isDisabilityVisible: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(ShowDisabilityPath);
};

const hasDisabilityType: TGetValueFn<boolean> = ({ storage, bindingContext }) => {
    return !!storage.getValue(bindingContext.getParent().navigate(PrEmployeeTemporalEntity.PrDisabilityType));
};

const isPensionVisible: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(ShowPensionPath);
};

const isSocialInsuranceDiscountVisible: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(ShowSocialInsuranceDiscountLocalPath);
};

function isNotOfPensionType(...types: PrPensionTypeCode[]): TGetValueFn<boolean> {
    return (args) => {
        const bc = args.bindingContext.getParent();
        const pensionRow = args.storage.getValue(bc) as IPrEmployeeTemporalEntity;
        const type = pensionRow?.PensionTypeCode ?? pensionRow?.PensionType?.Code;
        return type && !types.includes(type);
    };
}

const isStudyVisible: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(ShowStudyPath);
};

const isForeignInsuranceCarrierVisible: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(ShowForeignInsuranceCarrierPath);
};

export const isForeigner: TGetValueFn<boolean> = (args) => {
    return args.storage.getValueByPath(PrEmployeeEntity.CitizenshipCode) !== CountryCode.CzechRepublic;
};

const isIDForeignCountry: TGetValueFn<boolean> = (args) => {
    const countryOfIssueCode = args.storage.getValueByPath(PrEmployeeEntity.IDCountryOfIssueCode);
    return isDefined(countryOfIssueCode) && countryOfIssueCode !== CountryCode.CzechRepublic;
};

const hasFreeAccessToLaborMarket: TGetValueFn<boolean> = (args) => {
    return !!args.storage.getValueByPath(createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.HasFreeAccessToLaborMarket));
};

const hasWorkPermitWorkPermitType: TGetValueFn<boolean> = (args) => {
    const workPermitTypeCode = args.storage.getValueByPath(createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.WorkPermitType), { useDirectValue: false });
    return workPermitTypeCode === PrWorkPermitTypeCode.WorkPermit;
};

const hasSocialInsuranceDiscountSaved: TGetValueFn<boolean> = (args) => {
    const storage = args.storage as FormStorage<IPrEmployeeEntity, IEmployeeFormCustomData>;
    return !!storage.data.origEntity?.CurrentTemporalPropertyBag?.PrSocialInsuranceDiscountTypeCode;
};

const isSavedLineItem = ({ bindingContext }: IGetValueArgs) => {
    const collectionBc = bindingContext.getParentCollection();
    return !collectionBc.isNew();
};

const isOtherIdentifierVisible = (args: IGetValueArgs) => {
    return args.storage.getValueByPath(ShowOtherIdentifiers);
};

const isCzechCitizen = (args: IGetValueArgs) => {
    return args.storage.getValueByPath(PrEmployeeEntity.CitizenshipCode) === CountryCode.CzechRepublic;
};

export const hasCzechResidency = (args: IGetValueArgs): boolean => {
    return args.storage.getValueByPath(PrEmployeeEntity.CountryOfResidencyCode) === CountryCode.CzechRepublic;
};

const hasEmployment = (args: IGetValueArgs): boolean => {
    const storage = args?.storage as FormStorage<IPrEmployeeEntityExtended, IEmployeeFormCustomData>;
    return !!storage.getCustomData()?.employments?.length;
};

function getColorForEmployeeStatus(status: PrEmployeeStateCode): keyof DefaultTheme {
    switch (status) {
        case PrEmployeeStateCode.Current:
            return "C_SEM_text_good";
        case PrEmployeeStateCode.Future:
            return "C_SEM_text_warning";
        case PrEmployeeStateCode.Former:
            return "C_TEXT_primary";
        case PrEmployeeStateCode.None:
            return "C_SEM_text_bad";
    }
}

export const applyPensionDiscountPath = BindingContext.localContext("applyPensionDiscount");
export const employeeSummaryLocalPath = BindingContext.localContext(PrEmployeeEntity.FirstName);
export const EMPLOYEE_USER_TAB_ID = "UserTab";

const getPensionTemporalDialogSettings: () => TTemporalDialogSettings = () => ({
    dialogTitle: i18next.t("Employee:Form.PensionPlanDialog"),
    columns: [
        PrEmployeeTemporalEntity.PensionType,
        applyPensionDiscountPath,
        PrEmployeeTemporalEntity.DateWorkingRetireeSocialInsuranceDiscountFrom,
        PrEmployeeTemporalEntity.DatePensionFrom,
        PrEmployeeTemporalEntity.IsEarlyRetirement,
        PrEmployeeTemporalEntity.HasReducedRetirementAge,
    ],
    granularity: "day"
});

const DEFAULT_TAB_ID = "Default";

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrEmployee}Table`,
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                PrEmployeeEntity.NumberOurs,
                PrEmployeeEntity.FirstName,
                PrEmployeeEntity.LastName,
                PrEmployeeEntity.PersonalIdentificationNumber,
                PrEmployeeEntity.Email,
                PrEmployeeEntity.PhoneNumber,
                createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.HealthInsurance)
            ],
            filterDefinition: {
                [PrEmployeeEntity.FirstName]: {},
                [PrEmployeeEntity.LastName]: {},
                [PrEmployeeEntity.NumberOurs]: {},
                [PrEmployeeEntity.ExternalNumber]: {},
                [PrEmployeeEntity.PersonalIdentificationNumber]: {
                    formatter: BirthNumberFormatter,
                    parser: val => {
                        return val?.replace("/", "");
                    },

                },
                [PrEmployeeEntity.Email]: {},
                [PrEmployeeEntity.PhoneNumber]: {},
                ...getLabelsFilterDef(),
                [createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.HealthInsurance)]: {
                    fieldSettings: {
                        displayName: "Name"
                    }
                },
                [ATTACHMENTS_COLUMN_LOCAL_CONTEXT]: getAttachmentsFilterDef(),
            },
            isValueHelp: true
        }],
        initialSortBy: [{ id: PrEmployeeEntity.NumberOurs, sort: Sort.Desc }],
        columns: [
            PrEmployeeEntity.NumberOurs,
            PrEmployeeEntity.FirstName,
            PrEmployeeEntity.PrEmployeeStateCode,
            PrEmployeeEntity.PersonalIdentificationNumber,
            PrEmployeeEntity.Email,
            PrEmployeeEntity.PhoneNumber,
        ],
        columnDefinition: {
            [PrEmployeeEntity.NumberOurs]: {
                label: i18next.t("Employee:Table.Number")
            },
            [PrEmployeeEntity.FirstName]: {
                label: i18next.t("Employee:Table.EmployeeName"),
                width: BasicInputSizes.L,
                formatter: userNameWithAvatarTableFormatter,
                additionalProperties: [{ id: `/${PrEmployeeEntity.LastName}` }]
            },
            [PrEmployeeEntity.BirthLastName]: {},
            [PrEmployeeEntity.PrEmployeeStateCode]: {
                label: i18next.t("Employee:Table.EmployeeStatus"),
                formatter: (val: TValue) => {
                    const code = val as PrEmployeeStateCode;
                    const translatedValue = getEnumDisplayValue(EntityTypeName.PrEmployeeState, val as string);

                    return {
                        tooltip: translatedValue,
                        value: (
                            <ColoredText color={getColorForEmployeeStatus(code)}>
                                {translatedValue}
                            </ColoredText>
                        )
                    };
                }
            },
            [PrEmployeeEntity.Email]: {
                formatter: emailFormatter
            },
            [PrEmployeeEntity.PhoneNumber]: {
                formatter: phoneNumberFormatter
            },
            [PrEmployeeEntity.TitleBeforeName]: {},
            [PrEmployeeEntity.TitleAfterName]: {
                label: i18next.t("Employee:Form.TitleAfterName")
            },
            [PrEmployeeEntity.DateOfBirth]: {},
            [createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.HealthInsurance)]: {
                fieldSettings: {
                    displayName: "Name"
                }
            },
            [PrEmployeeEntity.ExternalNumber]: {},
            [PrEmployeeEntity.PersonalIdentificationNumber]: {
                label: i18next.t("Employee:Table.BirthNumber"),
                formatter: BirthNumberFormatter
            },
            [PrEmployeeEntity.Attachments]: getAttachmentsColumnDef(EntityTypeName.PrEmployee)
        },
        title: i18next.t("Employee:Title"),
        tabs: [
            { id: DEFAULT_TAB_ID, title: i18next.t("Employee:Table.AllNotFormer") },
            { id: PrEmployeeStateCode.Former, title: i18next.t("Employee:Table.FormerEmployeesTab") },
            { id: null, title: i18next.t("Employee:Table.AllEmployeesTab") }
        ],
        tabsSettings: {
            customFilter: selectedTab => {
                if (!selectedTab) {
                    return "";
                }
                if (selectedTab === DEFAULT_TAB_ID) {
                    return `${PrEmployeeEntity.PrEmployeeStateCode} in ('${PrEmployeeStateCode.Current}', '${PrEmployeeStateCode.Future}', '${PrEmployeeStateCode.None}')`;
                }
                return `${PrEmployeeEntity.PrEmployeeStateCode} eq '${selectedTab}'`;
            }
        },
        additionalProperties: [
            {
                id: PrEmployeeEntity.User,
                additionalProperties: [{
                    id: UserEntity.Status
                }, {
                    id: UserEntity.Id
                }]
            },
            {
                id: PrEmployeeEntity.TemporalPropertyBag,
                additionalProperties: [
                    { id: PrEmployeeTemporalEntity.PrDisabilityTypeCode },
                    { id: PrEmployeeTemporalEntity.PrFormOfStudyTypeCode },
                    { id: PrEmployeeTemporalEntity.DateValidFrom },
                    { id: PrEmployeeTemporalEntity.DateValidTo }
                ]
            }
        ]
    };

    const summary: ISummaryItem[] = [
        {
            id: employeeSummaryLocalPath,
            formatter: (val, args) => {
                // show only saved employee name value in summary as it is on other forms
                const entity = (args.storage as FormStorage).data.origEntity as IPrEmployeeEntity;
                const name = (entity as IPrEmployeeEntity).FirstName;
                const formatted = userNameWithAvatarFormatter(name, { ...args, entity }, {
                    isBig: true
                });

                if (typeof formatted === "string") {
                    return formatted;
                }

                return formatted.value;
            }
        },
        { ...getNumberOursSummaryDef("Employee") },
        {
            id: createPath(PrEmployeeEntity.User, UserEntity.Status),
            label: i18next.t("Employee:Form.Portal"),
            isVisible: not(isNewEntity),
            updateFromLiveValue: true,
            iconInfo: {
                hasAction: (args: IGetValueArgs): boolean => {
                    const storage = args.storage as FormStorage<IPrEmployeeEntity, IEmployeeFormCustomData>;
                    const userStatus = storage.getValueByPath(createPath(PrEmployeeEntity.User, UserEntity.Status)) as IUserStatusEntity;
                    return userStatus?.Code === UserStatusCode.Invited;
                },
                onClick: (storage) => {
                    const userId = storage.getValueByPath(createPath(PrEmployeeEntity.User, UserEntity.Id));
                    resendUserInvitation(storage, userId).then();
                },
                icon: ResendIcon,
                title: i18next.t("Employee:Form.ResendInvitation")
            },
            formatter: (val: TValue) => {
                if (val) {
                    const status = val as IUserStatusEntity;
                    return status.Name;
                }
                return DASH_CHARACTER;
            },
            colorFormatter: (val: TValue) => {
                const status = val as IUserStatusEntity;
                return getUserStatusColor(status?.Code as UserStatusCode);
            }
        }
    ];

    const permanentAddressFields = getAddressFields(PrEmployeeEntity.PermanentAddress, {}, { addHouseStreetNumber: true });
    const taxResidencyAddressFields = getAddressFields(PrEmployeeEntity.TaxResidenceAddress, {
        isVisible: not(hasCzechResidency)
    }, { addHouseStreetNumber: true });

    const isCZNeighborCountry = (args: IGetValueArgs) => {
        const { storage } = args;
        const permCC = storage.getValueByPath(createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.CountryCode));
        return [CountryCode.Austria, CountryCode.Slovakia, CountryCode.Poland, CountryCode.Germany].includes(permCC);
    };

    const isNonCzPermanentAddress: TGetValueFn<boolean> = (args: IGetValueArgs) => {
        return args.storage.getValueByPath(createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.CountryCode)) !== CountryCode.CzechRepublic;
    };

    const isCzechAddressVisible: TGetValueFn<boolean> = (args) => {
        const isNeighbor = isCZNeighborCountry(args);
        return isNeighbor ? !!args.storage.getValueByPath(ShowCzechAddressPath) : isNonCzPermanentAddress(args);
    };

    const contactAddressFields = getAddressFields(PrEmployeeEntity.ContactAddress, {
        isVisible: isContactAddressVisible
    }, { addHouseStreetNumber: true });
    const czechAddressFields = getAddressFields(PrEmployeeEntity.CzechAddress, {
        // required -> https://solitea-cz.atlassian.net/browse/DEV-44342
        isVisible: isCzechAddressVisible,
        isRequired: true,
        affectedFields: [
            { id: createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.Street) },
            { id: createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.StreetNumber) },
            { id: createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.HouseNumber) },
            { id: createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.City) },
            { id: createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.PostalCode) },
            { id: createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.Country) },
        ]
    }, { addHouseStreetNumber: true });
    const foreignInsuranceAddressFields = getAddressFields(
        createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.Address), {
            isVisible: isForeignInsuranceCarrierVisible
        }, { addHouseStreetNumber: true });
    const bankAccountFields = { ...getBankAccountFieldsDef({ type: null, addPaymentMethod: false }) };

    const tmpFieldsDef = { ...TemporalValidityFieldsDef };

    const firstHealthInsuranceDateValidFromIsReadOnly = (args: IGetValueArgs) => {
        const storage = args.storage as FormStorage<IPrEmployeeEntity, ISmartTemporalPropertyDialogCustomData>;
        const dialogBc = storage.getCustomData().openedDialogBindingContext;
        const key = args.bindingContext.getParent().getKey();

        if (dialogBc?.getPath() !== PrEmployeeTemporalEntity.HealthInsurance
            // we are only interested in the calls made for FastEntry items in SmartTemporalPropertyDialog => those have key defined
            || isNotDefined(key)) {
            return false;
        }

        const item = storage.getValue(args.bindingContext.getParent());
        const itemIndex = storage.getEntity().TemporalPropertyBag?.findIndex(i => i === item);

        // first item in HealthInsurance should have DateValidFrom disabled
        return itemIndex === 0;
    };

    const origIsReadOnly = tmpFieldsDef["TemporalPropertyBag/DateValidFrom"].isReadOnly;
    tmpFieldsDef["TemporalPropertyBag/DateValidFrom"].isReadOnly = ifAny(origIsReadOnly, firstHealthInsuranceDateValidFromIsReadOnly);

    const disabilityTemporalDialogSettings: ITemporalPropertySettings["temporalDialog"] = {
        dialogTitle: i18next.t("Employee:Form.PlanDisabilityTitle"),
        columns: [
            PrEmployeeTemporalEntity.PrDisabilityType,
            PrEmployeeTemporalEntity.DateDisabilityFrom,
            PrEmployeeTemporalEntity.DateDisabilityTo,
            PrEmployeeTemporalEntity.IsZtpP
        ]
    };

    const form: IFormDef = {
        id: `${EntityTypeName.PrEmployee}Form`,
        translationFiles: getDefinitions.translationFiles,
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("Employee:NewEmployee"), storage.data.entity?.FirstName ? `${storage.data.entity.FirstName} ${storage.data.entity.LastName ?? ""}` : ""),
        isDeletable: true,
        saveDraftLocally: true,
        formControl: EmployeeFormView,
        files: {
            readOnlyAttachmentPaths: [PrEmployeeEntity.PrYearOperationPayrollSheets],
            readOnlyAttachmentFolderName: i18next.t("Employee:Form.PayrollSheetsFolderName"),
            ...AttachmentsDef
        },
        customHeaderActions: [{
            id: PrSubmissionTypeCode.EmployerDiscountOnSocialInsurance,
            label: i18next.t("Employee:Form.CreateOZUSPOJ"),
            iconName: "OZUSPOJ",
            isVisible: not(isNewEntity),
            isDisabled: not(hasSocialInsuranceDiscountSaved)
        }, {
            id: PrSubmissionTypeCode.EmployersMassHealthInsuranceNotification,
            label: i18next.t("Employee:Form.CreateHOZ"),
            iconName: "HOZ",
            isVisible: not(isNewEntity),
            isDisabled: false
        }, {
            id: TAX_PAYER_DECLARATION_ACTION,
            label: i18next.t("TaxpayerDeclaration:Title"),
            iconName: "TaxPayerDeclaration",
            isVisible: not(isNewEntity)
        }, {
            id: SEND_EMPLOYEE_INVITATION_ACTION,
            label: i18next.t("Employee:Form.InviteToEmployeePortal"),
            iconName: "InviteToCustomerPortal",
            isVisible: ifAll(not(isNewEntity), (args) => {
                const storage = args.storage as FormStorage<IPrEmployeeEntity, IEmployeeFormCustomData>;
                const userStatus = storage.getValueByPath(createPath(PrEmployeeEntity.User, UserEntity.Status)) as IUserStatusEntity;
                return !userStatus;
            })
        }, {
            id: SEND_PAYSLIP_PASSWORD_ACTION,
            label: i18next.t("Employee:Form.SendPasswordToEmployee"),
            iconName: "Send",
            isVisible: not(isNewEntity)
        }],
        summary,
        additionalProperties: [
            ...getAttachmentAdditionalProps(PrEmployeeEntity.PrYearOperationPayrollSheets),
            ...getTemporalAdditionalProperties(PrEmployeeTemporalEntity, PrEmployeeEntity),
            ...NumberRangeAdditionalProperties,
            { id: PrEmployeeEntity.SalaryCurrencyCode },
        ],
        fieldDefinition: {
            ...getNumberRangeFieldDefs("Employee"),
            [PrEmployeeEntity.FirstName]: {},
            [PrEmployeeEntity.LastName]: {},
            [PrEmployeeEntity.BirthLastName]: {},
            [PrEmployeeEntity.FormerLastNames]: {},
            [PrEmployeeEntity.MpsvIdentifier]: {
                width: BasicInputSizes.S,
            },
            [PrEmployeeEntity.PrSex]: {
                type: FieldType.ComboBox,
                fieldSettings: {
                    displayName: PrSexEntity.Name
                },
                width: BasicInputSizes.S
            },
            [PrEmployeeEntity.TitleBeforeName]: {
                width: BasicInputSizes.XS,
                groupedField: GroupedField.MultiStart,
                textAlign: TextAlign.Right
            },
            [PrEmployeeEntity.TitleAfterName]: {
                width: BasicInputSizes.XS,
                label: i18next.t("Employee:Form.TitleAfterName"),
                groupedField: GroupedField.MultiEnd,
                textAlign: TextAlign.Left
            },
            [PrEmployeeEntity.DateOfBirth]: {},
            [PrEmployeeEntity.PhoneNumber]: {
                ...PhoneNumberDef
            },
            [PrEmployeeEntity.Email]: {
                ...EmailDef,
                width: BasicInputSizes.XL,
            },
            [PrEmployeeEntity.ExternalNumber]: {},
            [PrEmployeeEntity.Labels]: getLabelsDef("Employee"),
            [PrEmployeeEntity.Citizenship]: {
                ...countryDef,
                width: BasicInputSizes.L,
                affectedFields: [
                    { id: PrEmployeeEntity.ForeignPersonalIdentificationNumber },
                    { id: PrEmployeeEntity.ForeignTaxIdentifier },
                    { id: PrEmployeeEntity.ForeignTaxIdentifierType },
                    { id: PrEmployeeEntity.PrIdentificationDocument },
                    { id: PrEmployeeEntity.EuHealthCareNumber },
                    { id: PrEmployeeEntity.EuSocialInsuranceNumber },
                    { id: PrEmployeeEntity.IDAuthorityOfIssue },
                    { id: ShowOtherIdentifiers },
                    { id: PrEmployeeEntity.ApplicableForeignLegislationCountry }
                ]
            },
            [PrEmployeeEntity.CountryOfBirth]: {
                ...countryDef,
                width: BasicInputSizes.L,
            },
            [PrEmployeeEntity.PrIdentificationDocument]: {
                type: FieldType.ComboBox,
                defaultValue: PrIdentityDocumentTypeCode.IDCard,
                isVisible: not(isCzechCitizen),
                isRequired: not(isCzechCitizen),
                fieldSettings: {
                    displayName: PrIdentityDocumentTypeEntity.Name,
                    itemsForRender: (items: ISelectItem[], args: IGetValueArgs) => {
                        if (args.storage.getValueByPath(PrEmployeeEntity.CitizenshipCode) === CountryCode.CzechRepublic) {
                            return items.filter(item => item.id !== PrIdentityDocumentTypeCode.Passport);
                        }
                        return items.sort((a, b) =>
                            (a.id === PrIdentityDocumentTypeCode.Other ? 1 : (b.id === PrIdentityDocumentTypeCode.Other ? -1 : 0)));
                    }
                },
                groupedField: GroupedField.WrappedMultiStart
            },
            [PrEmployeeEntity.IDCountryOfIssue]: {
                ...countryDef,
                isVisible: not(isCzechCitizen),
                isRequired: not(isCzechCitizen),
                affectedFields: [{ id: PrEmployeeEntity.PersonalIdentificationNumber }, { id: PrEmployeeEntity.IDAuthorityOfIssue }],
                fieldSettings: {
                    ...countryDef.fieldSettings
                }
            },
            [PrEmployeeEntity.IDAuthorityOfIssue]: {
                label: i18next.t("Employee:Form.IssuedBy"),
                isRequired: isIDForeignCountry,
                isVisible: isIDForeignCountry,
                width: BasicInputSizes.M
            },
            [PrEmployeeEntity.CountryOfResidency]: {
                ...countryDef,
                fieldSettings: {
                    ...countryDef.fieldSettings
                },
                width: BasicInputSizes.L,
                affectedFields: [
                    { id: PrEmployeeEntity.DateResidencyFrom },
                    { id: PrEmployeeEntity.ForeignTaxIdentifierType },
                    { id: PrEmployeeEntity.ForeignTaxIdentifier },
                    ...Object.keys(taxResidencyAddressFields).map(key => ({ id: key })),
                ],
                customizationData: {
                    dependents: [PrEmployeeEntity.DateResidencyFrom]
                }
            },
            [PrEmployeeEntity.DateResidencyFrom]: {
                label: i18next.t("Employee:Form.DateResidencyFrom"),
                width: BasicInputSizes.L,
                isRequired: true,
                isVisible: hasEmployment,
            },
            [PrEmployeeEntity.IDNumber]: {
                width: BasicInputSizes.S,
                isVisible: not(isCzechCitizen),
                isRequired: not(isCzechCitizen),
            },
            [PrEmployeeEntity.DateIDEndOfValidity]: {
                groupedField: GroupedField.WrappedMultiEnd,
                isVisible: not(isCzechCitizen),
            },
            [PrEmployeeEntity.PersonalIdentificationNumber]: {
                ...BirthNumberFieldDef,
                isRequired: isCzechCitizen
            },
            [ShowOtherIdentifiers]: {
                type: FieldType.Switch,
                label: i18next.t("Employee:Form.ShowOtherIdentifiers"),
                isVisible: isForeigner,
                fieldSettings: {
                    type: SwitchType.YesNo
                },
                affectedFields: OtherIdentifierFields.map(id => ({ id })),
                defaultValue: false
            },
            [PrEmployeeEntity.ForeignPersonalIdentificationNumber]: {
                isVisible: ifAll(isForeigner, isOtherIdentifierVisible)
            },
            [PrEmployeeEntity.ForeignTaxIdentifierType]: {
                ...getForeignTaxIdentifierTypeDef(),
                label: i18next.t("Employee:Form.ForeignTaxIdentifierType"),
                groupedField: GroupedField.MultiStart,
                isVisible: ifAll(isForeigner, isOtherIdentifierVisible, not(hasCzechResidency)),
            },
            [PrEmployeeEntity.ForeignTaxIdentifier]: {
                label: i18next.t("Employee:Form.ForeignTaxIdentifier"),
                isVisible: ifAll(isForeigner, isOtherIdentifierVisible, not(hasCzechResidency)),
                groupedField: GroupedField.MultiEnd,
            },
            [PrEmployeeEntity.EuHealthCareNumber]: {
                isVisible: ifAll(isForeigner, isOtherIdentifierVisible)
            },
            [PrEmployeeEntity.EuSocialInsuranceNumber]: {
                isVisible: ifAll(isForeigner, isOtherIdentifierVisible)
            },
            [PrEmployeeEntity.ApplicableForeignLegislationCountry]: {
                ...countryDef,
                defaultValue: null,
                width: BasicInputSizes.XL,
                isVisible: isForeigner,
                fieldSettings: {
                    ...countryDef.fieldSettings,
                    noRecordText: i18next.t("Employee:Form.NoApplicableLegislation")
                }
            },
            [createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.HasFreeAccessToLaborMarket)]: {
                type: FieldType.Switch,
                isVisible: isForeigner,
                defaultValue: false,
                fieldSettings: {
                    type: SwitchType.YesNo
                },
                affectedFields: [
                    { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.FreeAccessType) },
                    { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.WorkPermitType) },
                    { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.WorkPermitNumber) },
                    { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.PermitIssuingOffice) },
                    { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.DateValidFrom) },
                    { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.DateValidTo) }
                ],
            },
            [createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.FreeAccessType)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.XL,
                isVisible: ifAll(hasFreeAccessToLaborMarket, isForeigner),
                isRequired: true,
                fieldSettings: {
                    displayName: MpsvFreeAccessToLaborMarketTypeEntity.Name,
                }
            },
            [createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.WorkPermitType)]: {
                type: FieldType.ComboBox,
                groupedField: GroupedField.WrappedMultiStart,
                defaultValue: PrWorkPermitTypeCode.WorkPermit,
                isVisible: ifAll(isForeigner, not(hasFreeAccessToLaborMarket)),
                isRequired: true,
                fieldSettings: {
                    displayName: PrWorkPermitTypeEntity.Name,
                    transformFetchedItems: transformWorkPermitTypeItems
                },
                affectedFields: [
                    { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.PermitIssuingOffice) },
                ]
            },
            [createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.WorkPermitNumber)]: {
                isVisible: ifAll(isForeigner, not(hasFreeAccessToLaborMarket)),
                isRequired: true,
            },
            [createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.PermitIssuingOffice)]: {
                type: FieldType.ComboBox,
                isVisible: ifAll(isForeigner, not(hasFreeAccessToLaborMarket), hasWorkPermitWorkPermitType),
                isRequired: true,
                fieldSettings: {
                    displayName: LORegionalOfficeEntity.Name,
                },
            },
            [createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.DateValidFrom)]: {
                isVisible: ifAll(isForeigner, not(hasFreeAccessToLaborMarket)),
                isRequired: true,
            },
            [createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.DateValidTo)]: {
                isVisible: ifAll(isForeigner, not(hasFreeAccessToLaborMarket)),
                isRequired: true,
                groupedField: GroupedField.WrappedMultiEnd
            },
            ...permanentAddressFields,
            [createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.Country)]: {
                ...permanentAddressFields[createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.Country)],
                width: BasicInputSizes.L,
                fieldSettings: {
                    ...permanentAddressFields[createPath(PrEmployeeEntity.PermanentAddress, PrEmployeePermanentAddressEntity.Country)].fieldSettings
                },
                affectedFields: [
                    ...Object.keys(czechAddressFields).map(key => ({ id: key })),
                    { id: ShowCzechAddressPath }
                ]
            },
            ...contactAddressFields,
            [createPath(PrEmployeeEntity.ContactAddress, PrEmployeeContactAddressEntity.Country)]: {
                ...contactAddressFields[createPath(PrEmployeeEntity.ContactAddress, PrEmployeeContactAddressEntity.Country)],
                width: BasicInputSizes.L,
                fieldSettings: {
                    ...contactAddressFields[createPath(PrEmployeeEntity.ContactAddress, PrEmployeeContactAddressEntity.Country)].fieldSettings
                }
            },
            ...taxResidencyAddressFields,
            [createPath(PrEmployeeEntity.TaxResidenceAddress, PrEmployeeTaxResidenceAddressEntity.Country)]: {
                ...taxResidencyAddressFields[createPath(PrEmployeeEntity.TaxResidenceAddress, PrEmployeeTaxResidenceAddressEntity.Country)],
                isReadOnly: true
            },
            ...czechAddressFields,
            [createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.Country)]: {
                ...czechAddressFields[createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.Country)],
                width: BasicInputSizes.L,
                isReadOnly: true,
                defaultValue: CountryCode.CzechRepublic,
                fieldSettings: {
                    ...czechAddressFields[createPath(PrEmployeeEntity.CzechAddress, PrEmployeeCzechAddressEntity.Country)].fieldSettings
                }
            },
            ...bankAccountFields,
            [createPath(PrEmployeeEntity.Dependents, PrEmployeeDependentPersonEntity.FirstName)]: {},
            [createPath(PrEmployeeEntity.Dependents, PrEmployeeDependentPersonEntity.LastName)]: {},
            [createPath(PrEmployeeEntity.Dependents, PrEmployeeDependentPersonEntity.DateOfBirth)]: {},
            [createPath(PrEmployeeEntity.Dependents, PrEmployeeDependentPersonEntity.PersonalIdentificationNumber)]: {
                ...BirthNumberFieldDef
            },
            [createPath(PrEmployeeEntity.Dependents, PrEmployeeDependentPersonEntity.Relation)]: {
                type: FieldType.ComboBox,
                defaultValue: PrEmployeeDependentPersonRelationCode.Child,
                fieldSettings: {
                    displayName: "Name",
                }
            },
            [createPath(PrEmployeeEntity.Dependents, PrEmployeeDependentPersonEntity.DateValidFrom)]: {
                defaultValue: () => getUtcDayjs(),
                fieldSettings: {
                    showSpecialValue: DateRangeSpecialValue.WithoutStart
                }
            },
            [createPath(PrEmployeeEntity.Dependents, PrEmployeeDependentPersonEntity.DateValidTo)]: {
                defaultValue: DATE_MAX,
                fieldSettings: {
                    showSpecialValue: DateRangeSpecialValue.WithoutEnd
                }
            },
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.DatePensionFrom,
                definition: {
                    fieldSettings: {
                        temporalDialog: getPensionTemporalDialogSettings()
                    }
                },
                currentDefinition: {
                    isRequired: true,
                    isVisible: isPensionVisible
                },
                withOpeningButton: false
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: applyPensionDiscountPath,
                definition: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Employee:Form.ApplyPensionDiscount"),
                    isDisabled: isNotOfPensionType(PrPensionTypeCode.OldAge, PrPensionTypeCode.ForeignOfAnOldageNature),
                    defaultValue: false,
                    fieldSettings: {
                        items: getSimpleBoolSelectItems(),
                        temporalDialog: getPensionTemporalDialogSettings()
                    }
                },
                currentDefinition: {
                    isRequired: true,
                    isVisible: isPensionVisible,
                },
                withOpeningButton: false
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.DateWorkingRetireeSocialInsuranceDiscountFrom,
                definition: {
                    type: FieldType.MonthYear,
                    label: i18next.t("Employee:Form.ApplyFrom"),
                    isDisabled: ifAny(isNotOfPensionType(PrPensionTypeCode.OldAge, PrPensionTypeCode.ForeignOfAnOldageNature), (args: IGetValueArgs) => {
                        return !args.storage.getValue(args.bindingContext.getParent().navigate(applyPensionDiscountPath));
                    }),
                    fieldSettings: {
                        temporalDialog: getPensionTemporalDialogSettings()
                    }
                },
                currentDefinition: {
                    isVisible: isPensionVisible,
                    isRequired: args => {
                        return args.storage.getValue(args.bindingContext.getParent().navigate(applyPensionDiscountPath));
                    }
                },
                withOpeningButton: false
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.IsEarlyRetirement,
                definition: {
                    type: FieldType.ComboBox,
                    isRequired: false,
                    label: i18next.t("Employee:Form.IsEarlyRetirement"),
                    width: BasicInputSizes.S,
                    defaultValue: false,
                    isDisabled: isNotOfPensionType(PrPensionTypeCode.OldAge),
                    fieldSettings: {
                        items: getSimpleBoolSelectItems(),
                        temporalDialog: getPensionTemporalDialogSettings()
                    }
                },
                currentDefinition: {
                    isVisible: isPensionVisible,
                    isRequired: isPensionVisible
                },
                withOpeningButton: false
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.HasReducedRetirementAge,
                definition: {
                    type: FieldType.ComboBox,
                    isRequired: false,
                    label: i18next.t("Employee:Form.HasReducedRetirementAge"),
                    width: BasicInputSizes.S,
                    groupedField: GroupedField.WrappedMultiEnd,
                    defaultValue: false,
                    isDisabled: isNotOfPensionType(PrPensionTypeCode.OldAge),
                    fieldSettings: {
                        items: getSimpleBoolSelectItems(),
                        temporalDialog: getPensionTemporalDialogSettings()
                    }
                },
                currentDefinition: {
                    isVisible: isPensionVisible,
                    isRequired: isPensionVisible
                },
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.PensionType,
                definition: {
                    type: FieldType.ComboBox,
                    groupedField: GroupedField.WrappedMultiStart,
                    defaultValue: PrPensionTypeCode.OldAge,
                    fieldSettings: {
                        displayName: PrPensionTypeEntity.Name,
                        transformFetchedItems: (items: ISelectItem[]) => items.filter(i => i.id !== PrPensionTypeCode.DoesNotReceive),
                        temporalDialog: getPensionTemporalDialogSettings()
                    },
                    affectedFields: [
                        { id: applyPensionDiscountPath, navigateFromParent: true },
                        { id: PrEmployeeTemporalEntity.IsEarlyRetirement, navigateFromParent: true },
                        { id: PrEmployeeTemporalEntity.HasReducedRetirementAge, navigateFromParent: true }
                    ]
                },
                currentDefinition: {
                    isVisible: isPensionVisible,
                    isRequired: isPensionVisible
                },
                withOpeningButton: false
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.HealthInsurance,
                definition: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Employee:Form.HealthInsuranceCompany"),
                    fieldSettings: {
                        displayName: PrHealthInsuranceCompanyEntity.ShortName,
                        shouldDisplayAdditionalColumns: true,
                        temporalDialog: {
                            dialogTitle: i18next.t("Employee:Form.PlanHealthInsuranceTitle"),
                            dateType: FieldType.MonthYear,
                            withoutGaps: true,
                            cannotBeEnded: true,
                            granularity: "month",
                            isCalendarDateDisabledDateValidFrom: (date: Date, unit: TDisabledDateUnit) => {
                                if (unit !== "month") {
                                    return false;
                                }

                                // only allow January and July to be selected
                                return ![0, 6].includes(date.getMonth());
                            },
                            isCalendarDateDisabledDateValidTo: (date: Date, unit: TDisabledDateUnit) => {
                                if (unit !== "month") {
                                    return false;
                                }

                                // only allow December and June to be selected
                                return ![11, 5].includes(date.getMonth());
                            },
                            getCustomDateGranularity: (date: Dayjs/*, next: boolean*/): Dayjs => {
                                // note: next is not relevant as we always return next here
                                const rootDay = date.startOf("month").startOf("day");

                                // after 1st of July we set January of next year
                                if (rootDay.month() >= 6) {
                                    // next January
                                    return rootDay.add(1, "year").startOf("year");
                                }

                                // otherwise we set 1st of July of current year
                                return rootDay.set("month", 6);
                            }
                        }
                    },
                    filter: {
                        // select: `${PrHealthInsuranceCompanyEntity.PrInsuranceCompanyTypeCode} eq '${PrInsuranceCompanyTypeCode.HealthInsuranceCompany}'`
                    },
                    columns: [
                        { id: PrHealthInsuranceCompanyEntity.HealthInsuranceCompanyCode },
                        {
                            id: PrHealthInsuranceCompanyEntity.ShortName,
                            label: i18next.t("Employee:Form.Shortcut")
                        },
                        { id: PrHealthInsuranceCompanyEntity.Name }
                    ],
                    additionalProperties: [
                        { id: PrHealthInsuranceCompanyEntity.HealthInsuranceCompanyCode }
                    ],
                    width: BasicInputSizes.XL
                }
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.PrDisabilityType,
                definition: {
                    type: FieldType.ComboBox,
                    groupedField: GroupedField.WrappedMultiStart,
                    width: BasicInputSizes.L,
                    fieldSettings: {
                        displayName: PrDisabilityTypeEntity.Name,
                        temporalDialog: disabilityTemporalDialogSettings,
                        noRecordText: i18next.t("Common:Select.NoRecord")
                    },
                    affectedFields: [
                        { id: PrEmployeeTemporalEntity.IsZtpP, navigateFromParent: true },
                        { id: PrEmployeeTemporalEntity.DateDisabilityFrom, navigateFromParent: true },
                        { id: PrEmployeeTemporalEntity.DateDisabilityTo, navigateFromParent: true }
                    ]
                },
                currentDefinition: {
                    isVisible: isDisabilityVisible
                },
                withOpeningButton: false
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.IsZtpP,
                definition: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Employee:Form.IsZtpP"),
                    defaultValue: false,
                    width: BasicInputSizes.XS,
                    groupedField: GroupedField.WrappedMultiEnd,
                    isDisabled: ({ storage, bindingContext }) => {
                        const disabilityType = storage.getValue(bindingContext.getParent().navigate(PrEmployeeTemporalEntity.PrDisabilityType));
                        return !disabilityType;
                    },
                    fieldSettings: {
                        temporalDialog: disabilityTemporalDialogSettings,
                        items: getSimpleBoolSelectItems()
                    }
                },
                currentDefinition: {
                    isVisible: isDisabilityVisible
                }
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.DateDisabilityFrom,
                definition: {
                    isDisabled: not(hasDisabilityType),
                    isRequired: hasDisabilityType,
                    fieldSettings: {
                        temporalDialog: disabilityTemporalDialogSettings
                    }
                },
                currentDefinition: {
                    isVisible: isDisabilityVisible
                },
                withOpeningButton: false
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.DateDisabilityTo,
                definition: {
                    isDisabled: not(hasDisabilityType),
                    fieldSettings: {
                        temporalDialog: disabilityTemporalDialogSettings
                    }
                },
                currentDefinition: {
                    isVisible: isDisabilityVisible
                },
                withOpeningButton: false
            }),
            // Study is temporarily hidden, we default it to "No" and keep the definition for future
            // ...getTemporalPropertyFieldDefinition({
            //     propName: PrEmployeeTemporalEntity.PrFormOfStudyType,
            //     definition: {
            //         type: FieldType.ComboBox,
            //         fieldSettings: {
            //             displayName: "Name",
            //             temporalDialog: {
            //                 dialogTitle: i18next.t("Employee:Form.PlanStudyTitle"),
            //                 granularity: "day"
            //             },
            //             noRecordText: i18next.t("Common:Select.NoRecord")
            //         }
            //     },
            //     currentDefinition: {
            //         isVisible: isStudyVisible
            //     }
            // }),
            [PreviousEmploymentDurationYearsPath]: {
                type: FieldType.NumberInput,
                width: BasicInputSizes.S,
                fieldSettings: {
                    unit: (args: IGetValueArgs) => {
                        const previousEmploymentDurationDays = args.storage.getValueByPath(PrEmployeeEntity.PreviousEmploymentDurationDays) as number;
                        const count = isNotDefined(previousEmploymentDurationDays) ? 0 : Math.floor(previousEmploymentDurationDays / 365);

                        return args.storage.t("Common:Time.Year", { count });
                    }
                },
                customizationData: {
                    useForCustomization: false
                },
                labelStatus: LabelStatus.Removed,
                useForValidation: true,
                affectedFields: [
                    { id: PreviousEmploymentDurationDaysPath }
                ]
            },
            [PrEmployeeEntity.PrEducation]: {
                ...educationDef,
            },
            [PreviousEmploymentDurationDaysPath]: {
                type: FieldType.NumberInput,
                width: BasicInputSizes.S,
                fieldSettings: {
                    unit: (args: IGetValueArgs) => {
                        const previousEmploymentDurationDays = args.storage.getValueByPath(PrEmployeeEntity.PreviousEmploymentDurationDays) as number;
                        const count = isNotDefined(previousEmploymentDurationDays) ? 0 : previousEmploymentDurationDays % 365;

                        return args.storage.t("Common:Time.Days", { count });
                    }
                },
                customizationData: {
                    useForCustomization: false
                },
                labelStatus: LabelStatus.Removed,
                useForValidation: true,
                affectedFields: [
                    { id: PreviousEmploymentDurationYearsPath }
                ]
            },
            [PrEmployeeEntity.PreviousEmploymentDurationDays]: {
                render: (args: IFieldDefFn) => {
                    return (
                        <Field label={i18next.t("Employee:Form.PreviousEmploymentDuration")}>
                            <FieldsWrapper isWithoutWrap smallerGap>
                                <SmartField
                                    storage={args.storage}
                                    bindingContext={args.storage.data.bindingContext.navigate(PreviousEmploymentDurationYearsPath)}
                                    onChange={args.props.parentProps.onChange}
                                    onBlur={args.props.parentProps.onBlur}
                                    fieldProps={{
                                        isSharpRight: true
                                    }}
                                />
                                <SmartField
                                    storage={args.storage}
                                    onChange={args.props.parentProps.onChange}
                                    onBlur={args.props.parentProps.onBlur}
                                    bindingContext={args.storage.data.bindingContext.navigate(PreviousEmploymentDurationDaysPath)}
                                    fieldProps={{
                                        isSharpLeft: true
                                    }}/>
                            </FieldsWrapper>
                        </Field>
                    );
                }
            },
            // temporarily hidden, https://solitea-cz.atlassian.net/browse/DEV-43663
            // [PrEmployeeEntity.SalaryCurrency]: {
            //     ...CurrencyDef
            // },
            [PrEmployeeEntity.PasswordForPayslip]: {
                type: FieldType.Password,
                label: i18next.t("Employee:Form.PasswordForPayslip"),
                defaultValue: () => {
                    return generatePassword();
                }
            },
            [ShowCzechAddressPath]: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.YesNo
                },
                customizationData: {
                    useForCustomization: false
                },
                isVisible: isCZNeighborCountry,
                affectedFields: Object.keys(czechAddressFields).map(key => ({ id: key }))
            },
            [ShowContactAddressPath]: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.YesNo
                },
                customizationData: {
                    useForCustomization: false
                },
                affectedFields: Object.keys(contactAddressFields).map(key => ({ id: key }))
            },
            [ShowDisabilityPath]: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.YesNo
                },
                customizationData: {
                    useForCustomization: false
                },
                affectedFields: [
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.PrDisabilityType) }
                ]
            },
            [ShowPensionPath]: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.YesNo
                },
                customizationData: {
                    useForCustomization: false
                },
                affectedFields: [
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.PensionType) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, applyPensionDiscountPath) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.DateWorkingRetireeSocialInsuranceDiscountFrom) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.DatePensionFrom) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.IsEarlyRetirement) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.HasReducedRetirementAge) },
                ]
            },
            // [ShowStudyPath]: {
            //     type: FieldType.Switch,
            //     fieldSettings: {
            //         type: SwitchType.YesNo
            //     },
            //     customizationData: {
            //         useForCustomization: false
            //     },
            //     affectedFields: [
            //         { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.PrFormOfStudyType) }
            //     ]
            // },
            // ------ ForeignInsuranceCarrier ------
            [ShowForeignInsuranceCarrierPath]: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.YesNo
                },
                customizationData: {
                    useForCustomization: false
                },
                affectedFields: [
                    { id: createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.InsuranceType) },
                    { id: createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.Name) },
                    ...(getAddressGroup(createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.Address), {
                        addHouseStreetNumber: true
                    })[0]),
                    { id: createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.InsuranceNumber) },
                    { id: createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.SectorType) },
                ]
            },
            ...foreignInsuranceAddressFields,
            [createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.Address, ForeignInsuranceCarrierAddressEntity.Country)]: {
                ...foreignInsuranceAddressFields[createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.Address, ForeignInsuranceCarrierAddressEntity.Country)],
                defaultValue: null,
            },
            [createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.InsuranceType)]: {
                type: FieldType.SegmentedButton,
                defaultValue: PrForeignInsuranceCarrierTypeCode.Previous,
                fieldSettings: {
                    items: getEnumSelectItems(EntityTypeName.PrForeignInsuranceCarrierType)
                        .filter(code => code.id !== PrForeignInsuranceCarrierTypeCode.None)
                },
                isVisible: isForeignInsuranceCarrierVisible,
            },
            [createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.Name)]: {
                width: BasicInputSizes.L,
                isVisible: isForeignInsuranceCarrierVisible,
            },
            [createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.InsuranceNumber)]: {
                width: BasicInputSizes.L,
                isVisible: isForeignInsuranceCarrierVisible,
            },
            [createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.SectorType)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.L,
                defaultValue: PrForeignInsuranceCarrierSectorTypeCode.All,
                fieldSettings: {
                    displayName: PrForeignInsuranceCarrierSectorTypeEntity.Name
                },
                isVisible: isForeignInsuranceCarrierVisible,
            },
            // ----- Social Insurance Discount -----
            [ShowSocialInsuranceDiscountLocalPath]: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.YesNo
                },
                customizationData: {
                    useForCustomization: false
                },
                affectedFields: [
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.PrSocialInsuranceDiscountType) }
                ]
            },
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.PrSocialInsuranceDiscountType,
                definition: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Employee:Form.SocialInsuranceDiscountType"),
                    isRequired: isSocialInsuranceDiscountVisible,
                    isVisible: isSocialInsuranceDiscountVisible,
                    fieldSettings: {
                        displayName: PrSocialInsuranceDiscountTypeEntity.Name,
                        temporalDialog: {
                            dialogTitle: i18next.t("Employee:Form.SocialInsuranceDiscountTypeDialogTitle"),
                            granularity: "month"
                        }
                    },
                    width: BasicInputSizes.L
                }
            }),
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmployeeTemporalEntity.PrMinimumHealthInsuranceType,
                definition: {
                    type: FieldType.ComboBox,
                    defaultValue: PrMinimumHealthInsuranceTypeCode.Normal,
                    fieldSettings: {
                        displayName: "Name",
                        temporalDialog: {
                            dialogTitle: i18next.t("Employee:Form.MinimumHealthInsuranceTypeDialogTitle"),
                            granularity: "day",
                            withoutGaps: true,
                            cannotBeEnded: true
                        }
                    },
                    width: BasicInputSizes.M
                }
            }),
            [PrEmployeeEntity.IsMultipleDistrainmentRuleExempt]: {
                type: FieldType.Switch,
                label: i18next.t("Employee:Form.IsMultipleDistrainmentRuleExempt"),
                defaultValue: false,
                fieldSettings: {
                    type: SwitchType.YesNo
                },
            },
            // ----- /Social Insurance Discount -----
            [PrEmployeeEntity.PlaceOfBirth]: {
                isRequired: true // isCzechCitizen
            },
            ...tmpFieldsDef
        },
        groups: [
            {
                id: "main",
                rows: [[
                    { id: PrEmployeeEntity.TitleBeforeName }, { id: PrEmployeeEntity.FirstName }, { id: PrEmployeeEntity.LastName }, { id: PrEmployeeEntity.TitleAfterName },
                    { id: PrEmployeeEntity.MpsvIdentifier },
                    { id: PrEmployeeEntity.BirthLastName },
                    { id: PrEmployeeEntity.PrSex }, { id: PrEmployeeEntity.DateOfBirth },
                    { id: PrEmployeeEntity.Citizenship }, { id: PrEmployeeEntity.PrEducation },
                    { id: PrEmployeeEntity.PhoneNumber }, { id: PrEmployeeEntity.Email },
                ]]
            },
            {
                id: "identification",
                title: i18next.t("Employee:Form.IdentificationGroup"),
                rows: [
                    [
                        { id: PrEmployeeEntity.PrIdentificationDocument }, { id: PrEmployeeEntity.IDCountryOfIssue },
                        { id: PrEmployeeEntity.IDAuthorityOfIssue },
                        { id: PrEmployeeEntity.IDNumber }, { id: PrEmployeeEntity.DateIDEndOfValidity }
                    ],
                    [
                        { id: PrEmployeeEntity.PersonalIdentificationNumber }, { id: PrEmployeeEntity.CountryOfBirth }, { id: PrEmployeeEntity.PlaceOfBirth },
                    ],
                    [
                        { id: PrEmployeeEntity.CountryOfResidency }, { id: PrEmployeeEntity.DateResidencyFrom }, { id: PrEmployeeEntity.ApplicableForeignLegislationCountry }
                    ],
                    [
                        { id: ShowOtherIdentifiers }
                    ],
                    [
                        { id: PrEmployeeEntity.ForeignPersonalIdentificationNumber },
                        { id: PrEmployeeEntity.ForeignTaxIdentifierType },
                        { id: PrEmployeeEntity.ForeignTaxIdentifier },
                        { id: PrEmployeeEntity.EuHealthCareNumber },
                        { id: PrEmployeeEntity.EuSocialInsuranceNumber },
                    ]
                ]
            },
            {
                id: "accessToLaborMarket",
                title: i18next.t("Employee:Form.AccessToLaborMarketGroup"),
                rows: [
                    [{ id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.HasFreeAccessToLaborMarket) }],
                    [{ id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.FreeAccessType) }],
                    [
                        { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.WorkPermitType) },
                        { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.WorkPermitNumber) },
                        { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.PermitIssuingOffice) },
                        { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.DateValidFrom) },
                        { id: createPath(PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.DateValidTo) }
                    ]
                ]
            },
            {
                id: "addressPermanent",
                title: i18next.t("Employee:Form.AddressPermanentGroup"),
                rows: getAddressGroup(PrEmployeeEntity.PermanentAddress, {
                    multiline: true,
                    addHouseStreetNumber: true
                })
            },
            {
                id: "addressCzech",
                title: i18next.t("Employee:Form.AddressCzechGroup"),
                togglePropPath: ShowCzechAddressPath,
                rows: getAddressGroup(PrEmployeeEntity.CzechAddress, { multiline: true, addHouseStreetNumber: true })
            },
            {
                id: "addressTaxResidency",
                title: i18next.t("Employee:Form.AddressTaxResidencyGroup"),
                rows: getAddressGroup(PrEmployeeEntity.TaxResidenceAddress, {
                    multiline: true,
                    addHouseStreetNumber: true
                })
            },
            {
                id: "addressContact",
                title: i18next.t("Employee:Form.AddressContactGroup"),
                togglePropPath: ShowContactAddressPath,
                rows: getAddressGroup(PrEmployeeEntity.ContactAddress, { multiline: true, addHouseStreetNumber: true })
            },
            {
                // id "payment" is expected in the change handlers
                id: "payment",
                title: i18next.t("Employee:Form.BankAccountGroup"),
                rows: [
                    [{ id: SAVED_ACCOUNTS_PATH }],
                    [...paymentDetailFields]
                ]
                // todo: what about ABA number field?? It was removed here, but is it correct??
            },
            {
                id: "healthInsurance",
                title: i18next.t("Employee:Form.HealthInsuranceGroup"),
                rows: [
                    [{ id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.HealthInsurance) }]
                ]
            },
            {
                id: "foreignInsuranceCarrier",
                togglePropPath: ShowForeignInsuranceCarrierPath,
                title: i18next.t("Employee:Form.ForeignInsuranceCarrier"),
                rows: [
                    [
                        { id: createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.InsuranceType) },
                        { id: createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.Name) }
                    ],
                    ...getAddressGroup(createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.Address), {
                        addHouseStreetNumber: true
                    }),
                    [
                        { id: createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.InsuranceNumber) },
                        { id: createPath(PrEmployeeEntity.ForeignInsuranceCarrier, PrForeignInsuranceCarrierEntity.SectorType) },
                    ]
                ]
            },
            {
                id: "dependents",
                title: i18next.t("Employee:Form.Dependents"),
                lineItems: {
                    collection: PrEmployeeEntity.Dependents,
                    order: "Id",
                    canReorder: false,
                    isItemCloneable: false,
                    isGroupToggleable: true,
                    columns: [
                        { id: PrEmployeeDependentPersonEntity.FirstName }, { id: PrEmployeeDependentPersonEntity.LastName },
                        { id: PrEmployeeDependentPersonEntity.DateOfBirth }, { id: PrEmployeeDependentPersonEntity.PersonalIdentificationNumber },
                        { id: PrEmployeeDependentPersonEntity.Relation },
                        { id: PrEmployeeDependentPersonEntity.DateValidFrom }, { id: PrEmployeeDependentPersonEntity.DateValidTo }
                    ]
                }
            },
            {
                id: "disabilityType",
                title: i18next.t("Employee:Form.DisabilityGroup"),
                togglePropPath: ShowDisabilityPath,
                rows: [[
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.PrDisabilityType) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.DateDisabilityFrom) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.DateDisabilityTo) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.IsZtpP) }
                ]]
            },
            // {
            //     id: "studyType",
            //     title: i18next.t("Employee:Form.StudyGroup"),
            //     togglePropPath: ShowStudyPath,
            //     rows: [
            //         [{ id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.PrFormOfStudyType) }]
            //     ]
            // },
            {
                id: "pensionType",
                title: i18next.t("Employee:Form.PensionGroup"),
                togglePropPath: ShowPensionPath,
                rows: [[
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.PensionType) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, applyPensionDiscountPath) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.DateWorkingRetireeSocialInsuranceDiscountFrom) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.DatePensionFrom) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.IsEarlyRetirement) },
                    { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.HasReducedRetirementAge) },
                ]]
            },
            {
                id: "socialInsuranceDiscount",
                title: i18next.t("Employee:Form.SocialInsuranceDiscount"),
                togglePropPath: ShowSocialInsuranceDiscountLocalPath,
                rows: [
                    [{ id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.PrSocialInsuranceDiscountType) }]
                ]
            },
            {
                id: "settings",
                title: i18next.t("Employee:Form.Settings"),
                rows: [
                    [
                        { id: PrEmployeeEntity.PreviousEmploymentDurationDays },
                        { id: createPath(PrEmployeeEntity.CurrentTemporalPropertyBag, PrEmployeeTemporalEntity.PrMinimumHealthInsuranceType) },
                        { id: PrEmployeeEntity.IsMultipleDistrainmentRuleExempt }
                    ],
                    /*[{ id: PrEmployeeEntity.SalaryCurrency }, ],*/
                ]
            },
            {
                id: "payslipSettings",
                title: i18next.t("Employee:Form.PayslipSettings"),
                rows: [
                    [{ id: PrEmployeeEntity.PasswordForPayslip }]
                ]
            },
            {
                id: "Tabs",
                tabs: [
                    {
                        id: "employment",
                        title: i18next.t("Employee:FormTab.Employments"),
                        table: {
                            id: "employeeEmploymentTable",
                            entitySet: EntitySetName.PrEmployments,
                            filter: (args: IGetValueArgs) => {
                                return `${PrEmploymentEntity.Employee}/Id eq ${args.storage.data.entity.Id}`;
                            },
                            initialSortBy: [{ id: PrEmploymentEntity.DateStart, sort: Sort.Asc }],
                            columns: [
                                {
                                    id: PrEmploymentEntity.NumberOurs,
                                    formatter: (val: TValue, {
                                        entity,
                                        storage
                                    }: IFormatOptions<IPrEmploymentEntity>): TCellValue => {
                                        const { Id } = entity;
                                        return getTableIntentLink(val as string, {
                                            route: `${ROUTE_EMPLOYMENT}/${Id}`,
                                            context: storage.context,
                                            storage: storage
                                        });
                                    }
                                },
                                {
                                    id: PrEmploymentEntity.Name,
                                    label: i18next.t("Employee:FormTab.EmploymentNameColumn"),
                                },
                                {
                                    id: PrEmploymentEntity.Type,
                                    fieldSettings: {
                                        displayName: PrEmploymentTypeEntity.Name
                                    }
                                },
                                {
                                    id: BindingContext.localContext("Status"),
                                    label: i18next.t("Employee:FormTab.StatusColumn"),
                                    fieldSettings: {
                                        disableSort: true
                                    },
                                    formatter: (val: TValue, args: IFormatOptions<IPrEmploymentEntity>) => {
                                        const status = getEmploymentStatus(args.entity);
                                        const isActive = status === EmploymentStatus.Active;
                                        const text = i18next.t(`Employment:Table.${status}`);
                                        const color = isActive ? "C_SEM_text_good" : "C_TEXT_primary";

                                        return {
                                            tooltip: text,
                                            value: (
                                                <ColoredText color={color}>
                                                    {text}
                                                </ColoredText>
                                            )
                                        };
                                    },
                                    additionalProperties: [
                                        { id: PrEmploymentEntity.DateStart },
                                        { id: PrEmploymentEntity.DateEnd },
                                    ]
                                },
                                { id: PrEmploymentEntity.DateStart },
                                { id: PrEmploymentEntity.DateEnd }
                            ]
                        }
                    },
                    {
                        id: "deductions",
                        title: i18next.t("Employee:FormTab.Deductions"),
                        table: {
                            id: "employeeDeductionsTable",
                            entitySet: EntitySetName.PrDeductions,
                            filter: (args: IGetValueArgs) => {
                                return `${PrDeductionEntity.CurrentTemporalPropertyBag}/${PrDeductionTemporalEntity.IsIncludingAllEmployees} eq true 
                                    OR ${PrDeductionEntity.Employee}/Id eq ${args.storage.data.entity.Id} 
                                    OR ${PrDeductionEntity.Employees}/any(x: x/Employee/Id eq ${args.storage.data.entity.Id})`;
                            },
                            initialSortBy: [{ id: PrDeductionEntity.NumberOurs, sort: Sort.Asc }],
                            columns: [
                                {
                                    id: PrDeductionEntity.NumberOurs,
                                    formatter: (val: TValue, {
                                        entity,
                                        storage
                                    }: IFormatOptions<IPrDeductionEntity>): TCellValue => {
                                        const { Id } = entity;
                                        return getTableIntentLink(val as string, {
                                            route: `${ROUTE_DEDUCTIONS}/${Id}`,
                                            context: storage.context,
                                            storage: storage
                                        });
                                    }
                                },
                                {
                                    id: PrDeductionEntity.Type,
                                    fieldSettings: {
                                        displayName: PrDeductionTypeEntity.Name
                                    }
                                },
                                { id: PrDeductionEntity.Name },
                                { id: PrDeductionEntity.DateStart },
                                { id: PrDeductionEntity.DateEnd }
                            ]
                        }
                    },
                    getSubmissionTableDefinitionForFormTabs(PrSubmissionEntity.EmployeeHeader),
                    {
                        id: EMPLOYEE_USER_TAB_ID,
                        title: i18next.t("Employee:FormTab.User"),
                        table: {
                            id: "employeeUserTable",
                            entitySet: EntitySetName.Users,
                            filter: (args: IGetValueArgs) => {
                                const userId = args.storage.getValueByPath(PrEmployeeEntity.User)?.Id;
                                if (!userId) {
                                    return "false";
                                }
                                return `${UserEntity.Id} eq ${args.storage.data.entity.User?.Id}`;
                            },
                            columns: [
                                {
                                    id: UserEntity.Name,
                                    label: i18next.t("Employee:FormTab.User"),
                                    additionalProperties: [{
                                        id: UserEntity.FirstName
                                    }, {
                                        id: UserEntity.LastName
                                    }],
                                    formatter: (val: TValue, args: IFormatOptions<IUserEntity>): TCellValue => {
                                        return userNameWithAvatarFormatter(val, args, {
                                            isInTable: true,
                                            withLink: true
                                        });
                                    }
                                },
                                {
                                    id: UserEntity.CompanyRoles,
                                    label: i18next.t("Employee:FormTab.CompanyRole"),
                                    textAlign: TextAlign.Left,
                                    formatter: (val: TValue, args: IFormatOptions<IUserEntity>): TCellValue => {
                                        const rolesForCompany = args.entity.CompanyRoles?.filter(role => role.Company.Id === args.storage.context.getCompanyId())?.map(role => role.CompanyRole?.CompanyRoleName);
                                        return rolesForCompany?.join(", ");
                                    },
                                    additionalProperties: [{
                                        id: createPath(UserCompanyRoleEntity.CompanyRole, CompanyRoleEntity.CompanyRoleName)
                                    }, {
                                        id: UserCompanyRoleEntity.Company
                                    }]
                                },
                                {
                                    id: UserEntity.Email,
                                    label: i18next.t("Employee:FormTab.CompanyEmail")
                                }
                            ]
                        }
                    },
                ]
            }
        ]
    };

    return {
        entitySet: EntitySetName.PrEmployees,
        table,
        form
    };
};

getDefinitions.translationFiles = [
    "Employee",
    "Employment",
    "Common",
    "Components",
    "NumberRange",
    "TaxpayerDeclaration",
    "Submission",
    "Users",
    getEnumNameSpaceName(EntityTypeName.PrForeignInsuranceCarrierType),
    getEnumNameSpaceName(EntityTypeName.UserStatus),
    getEnumNameSpaceName(EntityTypeName.PrEmployeeState)];
setDefByEntityType(EntityTypeName.PrEmployee, getDefinitions);
